import { flatMap } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { FormattedMessage, useIntl } from 'react-intl'
import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query'
import styled from 'styled-components'

import Button from '@/components/Button'
import ConfirmationOverlay from '@/components/ConfirmationOverlay'
import { Delete, Error, ImageGallery, Upload2 } from '@/components/Icons'
import { ImagePreview } from '@/components/ImagePreview'
import Loader from '@/components/Loading'
import LoadingSpinner from '@/components/LoadingSpinner/LoadingSpinner'
import Modal from '@/components/Modal'
import { getSpaceId } from '@/store/contextSubscriber'
import {
  fetchUploadRequestV2,
  requestDeleteAsset,
  uploadFileWithSignedUploadRequest
} from '@/utilities/assetUploadServices'
import fetch from '@/utilities/fetch'

import ImageLoadingSkeleton from './ImageLoadingSkeleton'
import messages from './messages'

const ASSET_URL = ''

const InnerContainer = styled.div`
  display: grid;
  grid-template-rows: auto 1fr auto;
`

const Heading = styled.div`
  padding: 15px;

  h1 {
    font-size: 1.5rem;
    display: inline-block;
  }
`

const GalleryScroller = styled.div`
  min-height: 300px;
  max-height: 500px;
  overflow-x: scroll;
`

const Gallery: any = styled.div`
  padding: 15px;
  display: grid;
  justify-content: ${({ centerContent }: any) => (centerContent ? 'space-evenly' : 'start')};
  align-content: space-around;
  grid-template-columns: repeat(auto-fit, 150px);
  grid-gap: 10px;

  .loader {
    height: auto;
  }
`

// Using flex here instead of grid as a "use what works best for the situation"
// https://www.smashingmagazine.com/2017/09/css-grid-gotchas-stumbling-blocks/
const ActionFooter = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  padding: 10px 10px 10px 30px;
  background: #ececec;
`

const ImageGalleryIcon = styled(ImageGallery)`
  height: 1.3rem;
  margin-right: 10px;
  color: var(--wg-dark-blue);
`

const ImageButton: any = styled(Button)`
  padding: 0;
  margin: 0;
  box-shadow: ${({ selected }: any) => (selected ? '0 0 10px var(--wg-dark-blue)' : '')};
  border-width: 2px;
  border-style: solid;
  border-color: ${({ selected }: any) => (selected ? 'var(--wg-dark-blue)' : 'transparent')};
  border-radius: 15px;
  height: 150px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 15px;
    display: inline-block;
    border: 5px solid white;
  }

  @media (min-width: 900px) {
    margin: 0;
  }
`

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  grid-gap: 15px;

  img,
  div {
    border-radius: 7px;
    height: 50px;
  }
`

const EmptyErrorPreview = styled.div`
  display: grid;
  place-items: center;
  border: 1px solid var(--wg-red);
  width: 50px;

  & svg {
    height: 1rem;
    width: auto;
  }
`

const ButtonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, auto);
  /* This will make the buttons appear at the bottom right (ltr languages) */
  justify-content: end;
  align-content: end;
`

const ErrorText = styled.p`
  color: var(--wg-red);
  padding: 0 5px;
`

const UploadSquare: any = styled.div`
  position: relative;
  cursor: ${({ canUpload, canSelect }: any) => (canUpload || canSelect ? 'pointer' : 'inherit')};
  display: grid;
  align-content: center;
  justify-content: center;

  border-width: 2px;
  box-shadow: ${({ isDragAccept }: any) => (isDragAccept ? '0 0 10px var(--wg-dark-blue)' : 'none')};
  border-style: ${({ selected }: any) => (selected ? 'solid' : 'dashed')};
  border-color: ${({ selected, canSelect }: any) => (canSelect && !selected ? 'var(--wg-dark-blue)' : 'transparent')};
  border-radius: 15px;
  height: 150px;
`

const UploadText = styled.p`
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--wg-blue-link);
`

const StyledUploadIcon = styled(Upload2)`
  height: 25px;
  margin: 0 0.25rem;
`

const UploadingOverlay = styled.div`
  position: absolute;
  top: 15px;
  left: 15px;
  width: calc(100% - 30px);
  background: rgba(255, 255, 255, 1);
  height: calc(100% - 30px);
  display: grid;
  grid-template-rows: auto auto;
  align-content: center;
  border-radius: 5px;
  border: 1px solid var(--wg-gray-border);
`

const OverlaySpinner = styled(LoadingSpinner)`
  margin: auto;
`

const StyledCancelButton = styled(Button)`
  min-height: 0;
  margin: 0;
  padding: 0;
  color: var(--wg-blue-link);
  text-decoration: underline;
`

const SmallErrorIcon: any = styled(Error)`
  margin: auto;
  height: 2rem;
`

const StyledDeleteButton = styled(Button)`
  min-height: 0;
  margin: 0;
  padding: 0;
  position: absolute;
  right: 15px;
  bottom: 15px;
`

const StyledDeleteIcon = styled(Delete)`
  fill: var(--wg-red);
  background: white;
  border-width: 1px;
  border-color: var(--wg-red);
  border-style: solid;
  border-radius: 35%;
  padding: 5px;
  width: 30px;
  height: 30px;
`

const ImageButtonImg = styled(ImagePreview)`
  width: 100%;
`

const StyledLoadingSkeleton = styled(ImageLoadingSkeleton)`
  height: 150px;
`

export type Image = {
  id: string
  url: string
  fileName?: string
}

type ImageLibraryModalProps = {
  isOpen?: boolean
  onChange?: (image: Image) => void
  onCancel?: () => void
  acceptedTypes?: Array<string>
}

const getImages = async ({ pageParam }: any) => {
  // Note that the :organizationId and :spaceId are replaced by the fetch component
  const resultingParams: any = { headers: { 'x-workgrid-space': getSpaceId() }, search: { limit: 20 } }
  if (pageParam) {
    resultingParams.search.cursor = pageParam
  }

  return await fetch(ASSET_URL, resultingParams, {
    setAuthorization: true,
    service: 'assetsV2'
  })
}

const ImageLibraryModal = ({
  onChange = () => {},
  onCancel = () => {},
  isOpen = false,
  acceptedTypes = ['png', 'gif', 'jpg', 'jpeg']
}: ImageLibraryModalProps) => {
  const intl = useIntl()

  const saveRef = useRef<any>()
  const [selectedImage, setSelectedImage] = useState<Image>()
  const [uploadedImage, setUploadedImage] = useState<Image | undefined>()
  const [uploadError, setUploadError] = useState<any | undefined>()
  const [uploading, setUploading] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<boolean>()
  const [isDeleting, setIsDeleting] = useState(false)
  const currentAssetId = useRef<string>()
  const cancelUpload = useRef<AbortController>()
  const attemptedFiles = useRef<Array<File>>()

  const queryClient = useQueryClient()

  const {
    isLoading: isLoadingList,
    isError: isListError,
    data: imageListResult,
    hasNextPage,
    fetchNextPage,
    refetch
  } = useInfiniteQuery({
    queryKey: ['imageGallery.images'],
    queryFn: getImages,
    getNextPageParam: lastPage => lastPage?.cursor
  })

  useEffect(() => {
    if (!isOpen) {
      setSelectedImage(undefined)
      setUploadedImage(undefined)
    }
  }, [isOpen])

  const { getRootProps, getInputProps, isDragAccept } = useDropzone({
    accept: acceptedTypes.map(a => `image/${a}`).join(', '),
    noClick: !!uploadedImage,
    noDrag: !!uploadedImage,
    maxFiles: 1,
    onDrop: (acceptedFiles: Array<File>) => {
      // Hold on to these acceptedFiles just in case we need to retry
      attemptedFiles.current = acceptedFiles
      if (acceptedFiles?.length) {
        const localUploadedImage = {
          id: 'optimistic-uploaded-image-id',
          url: URL.createObjectURL ? URL.createObjectURL(acceptedFiles[0]) : '', // URL.createObjectURL doesn't exist in jest land
          fileName: acceptedFiles[0].name
        }

        setUploadedImage(localUploadedImage)
        requestUpload(acceptedFiles, localUploadedImage)
      } else {
        setUploadError(
          <FormattedMessage
            {...messages.invalidUploadFileType}
            values={{
              types: acceptedTypes.join(', ')
            }}
          />
        )
      }
    }
  })

  // END of what to remove after we get the network stuff in place

  const requestUpload = async (acceptedFiles?: Array<File>, optimisticImage?: Image) => {
    setUploadError(undefined)
    setSelectedImage(undefined)

    const files = acceptedFiles || attemptedFiles.current

    if (files?.length) {
      const file: File = files[0]

      try {
        setUploading(true)
        const { postData, url, id: assetId }: any = await fetchUploadRequestV2(file)
        // Set the currentAssetId if we decide to remove this image (fires a delete)
        currentAssetId.current = assetId

        const { promise: fileUploadPromise, abortController } = uploadFileWithSignedUploadRequest(postData, file)
        // Save the ability to abort this upload
        cancelUpload.current = abortController

        fileUploadPromise
          .then(() => {
            attemptedFiles.current = undefined
            setUploadError(undefined)
            setUploading(false)
            // The "then" is still called even after the cancel is called, so only set this when things are complete
            if (cancelUpload.current) {
              const resultImage = {
                id: assetId,
                fileName: optimisticImage?.fileName || '',
                url
              }
              setUploadedImage(resultImage)
              setSelectedImage(resultImage)
            }
          })
          .catch(() => {
            // Request to delete the item since it failed to upload
            requestDeleteAsset(assetId)
            // Catch any exceptions that come from the uploadFile to S3 step
            setUploadError(intl.formatMessage(messages.uploadFailure))
            setUploadedImage(undefined)
          })
      } catch {
        // Sentry?
        setUploadError(intl.formatMessage(messages.uploadFailure))
        setUploadedImage(undefined)
      }
    } else {
      // We were asked to upload but none of the files are of the correct type
      setUploadError(
        intl.formatMessage(messages.invalidUploadFileType, {
          types: acceptedTypes?.join(', ')
        })
      )
      setUploadedImage(undefined)
    }
  }

  const onCancelUpload = () => {
    setUploading(false)
    setUploadedImage(undefined)

    // Request to delete the asset
    if (currentAssetId.current) {
      requestDeleteAsset(currentAssetId.current, { hardDelete: true })
    }

    cancelUpload.current?.abort()
    cancelUpload.current = undefined
  }

  const selectImage = (image?: Image) => {
    saveRef?.current?.focus()

    let clickedImage = image

    if (uploadedImage && image?.url === uploadedImage?.url) {
      // Check the uploadedImage as well
      clickedImage = uploadedImage
    }
    setSelectedImage(clickedImage)
  }

  const save = () => {
    if (selectedImage) {
      onChange(selectedImage)
      refetch()
    }
  }

  const cancel = () => {
    // If we have an uploaded image, ask to delete it
    if (currentAssetId.current) {
      requestDeleteAsset(currentAssetId.current)
    }
    onCancel()
  }

  const removeUploadedImage = () => {
    if (uploadedImage) {
      requestDeleteAsset(uploadedImage.id, { hardDelete: true })
    }

    if (selectedImage?.url === uploadedImage?.url) {
      setSelectedImage(undefined)
    }
    setUploadedImage(undefined)
  }

  const removeSelectedImage = async () => {
    if (selectedImage) {
      await requestDeleteAsset(selectedImage.id, { hardDelete: false })
    }
  }

  const totalImages = flatMap(imageListResult?.pages, page => page?.items)

  const { mutate } = useMutation({
    mutationFn: removeSelectedImage,

    onMutate: async () => {
      setIsDeleting(true)
      setConfirmDelete(false)

      // Cancel any queries that would interfere with an optimistic update
      await queryClient.cancelQueries({ queryKey: ['imageGallery.images'] })
      // Hold onto the previous state of the gallery in case of errors
      const previousGallery: any = queryClient.getQueryData(['imageGallery.images'])

      //Remove the deleted image used selectedImage.id
      const newImageListResult = previousGallery.pages.map((page: any) =>
        page.items.filter((value: Image) => value.id !== selectedImage?.id)
      )
      //Update the query with the image optimistically removed
      queryClient.setQueryData(['imageGallery.images'], {
        pageParams: [undefined],
        pages: [{ items: newImageListResult.flat() }]
      })
      return { previousGallery }
    },

    onError: (err, newImageGallery, context) => {
      // @ts-expect-error
      queryClient.setQueryData(['imageGallery.images'], context.previousGallery)
    },

    onSuccess: () => {
      setIsDeleting(false)
      setSelectedImage(undefined)
    }
  })

  return (
    <Modal isOpen={isOpen} onClose={onCancel}>
      <InnerContainer>
        {confirmDelete && (
          <ConfirmationOverlay
            isWarning={true}
            onConfirm={mutate}
            onCancel={() => setConfirmDelete(false)}
            message={intl.formatMessage(messages.confirmDeleteTitle)}
            subMessage={intl.formatMessage(messages.confirmDeleteSub)}
            onConfirmActionTitle={intl.formatMessage(messages.confirmButtonMessage)}
            onCancelActionTitle={intl.formatMessage(messages.cancelButtonMessage)}
          />
        )}
        <Heading>
          <ImageGalleryIcon />
          <h1 id={'image-gallery-modal-title'}>{intl.formatMessage(messages.galleryTitle)}</h1>
        </Heading>
        <GalleryScroller>
          <Gallery
            /* @ts-expect-error */
            centerContent={isLoadingList || isListError || isDeleting || imageListResult?.pages?.items?.length > 4}
          >
            {/* @ts-expect-error */}
            {(isLoadingList && !imageListResult?.pages?.items?.length) || isDeleting ? (
              <Loader />
            ) : isListError ? (
              <div style={{ textAlign: 'center' }}>
                <Error />
                <ErrorText>{intl.formatMessage(messages.galleryLoadError)}</ErrorText>
              </div>
            ) : (
              <>
                <UploadSquare
                  {...getRootProps()}
                  isDragAccept={isDragAccept}
                  selected={uploadedImage && selectedImage?.url === uploadedImage?.url}
                  canUpload={!uploadedImage}
                  canSelect={!(uploadedImage && selectedImage?.url !== uploadedImage?.url)}
                >
                  <input {...getInputProps()} aria-label={intl.formatMessage(messages.galleryUpload)} />
                  {uploadError ? (
                    <>
                      <SmallErrorIcon />
                      <ErrorText>{intl.formatMessage(messages.uploadError)}</ErrorText>
                      <UploadText>{intl.formatMessage(messages.retry)}</UploadText>
                    </>
                  ) : uploadedImage ? (
                    <>
                      {uploading && (
                        <UploadingOverlay>
                          <OverlaySpinner />
                          <StyledCancelButton color={'transparent'} onClick={onCancelUpload}>
                            {intl.formatMessage(messages.cancel)}
                          </StyledCancelButton>
                        </UploadingOverlay>
                      )}
                      <ImageButton
                        color={'transparent'}
                        onClick={() => selectImage({ id: '', url: uploadedImage?.url })}
                        selected={uploadedImage && selectedImage?.url === uploadedImage?.url}
                      >
                        <img src={uploadedImage.url} alt={uploadedImage.fileName} />
                      </ImageButton>
                      {!uploading && (
                        <StyledDeleteButton color={'transparent'} onClick={removeUploadedImage}>
                          <StyledDeleteIcon title={intl.formatMessage(messages.remove)} />
                        </StyledDeleteButton>
                      )}
                    </>
                  ) : (
                    <UploadText>
                      {/* Note, we send empty title ONLY when it's already described by appropriate labeling */}
                      <StyledUploadIcon title={''} />
                      <FormattedMessage {...messages.galleryUpload} />
                    </UploadText>
                  )}
                </UploadSquare>
                {totalImages?.map((image: Image) => (
                  <ImageButton
                    key={image?.id}
                    color={'transparent'}
                    onClick={() => selectImage(image)}
                    selected={selectedImage?.url === image?.url}
                  >
                    <ImageButtonImg src={image?.url} alt={image?.fileName} />
                  </ImageButton>
                ))}
                <StyledLoadingSkeleton loading={isLoadingList} hasNextPage={hasNextPage} onVisible={fetchNextPage} />
              </>
            )}
          </Gallery>
        </GalleryScroller>
        <ActionFooter>
          <PreviewGrid>
            {selectedImage ? (
              <>
                <img src={selectedImage.url} alt={selectedImage.fileName} />
                <p>{selectedImage.fileName}</p>
              </>
            ) : (
              uploadError && (
                <>
                  <EmptyErrorPreview>
                    <SmallErrorIcon />
                  </EmptyErrorPreview>
                  <ErrorText>{uploadError}</ErrorText>
                </>
              )
            )}
          </PreviewGrid>
          <ButtonGrid>
            <Button color={'secondary'} onClick={cancel}>
              {intl.formatMessage(messages.cancel)}
            </Button>

            <Button
              color={'warning'}
              ref={saveRef}
              onClick={() => setConfirmDelete(true)}
              disabled={!selectedImage || isLoadingList || isListError}
            >
              {intl.formatMessage(messages.delete)}
            </Button>

            <Button
              color={'primary'}
              ref={saveRef}
              onClick={save}
              disabled={!selectedImage || isLoadingList || isListError}
            >
              {intl.formatMessage(messages.save)}
            </Button>
          </ButtonGrid>
        </ActionFooter>
      </InnerContainer>
    </Modal>
  )
}

ImageLibraryModal.displayName = 'ImageGalleryModal'

export default ImageLibraryModal
export const ImageLibraryModalSansQuery = ImageLibraryModal // For Testing so we can mock the react-query provider
